/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import EnvironmentModel from 'cio/models/environment';
import { wait } from 'ember-animated';
import Sprite from 'ember-animated/-private/sprite';
import Ember from 'ember';
import { DELIVERY_METRICS_MEASURE_LABELS } from 'cio/utils/metrics/analysis';
import { underscore } from '@ember/string';
import actionInfo from 'cio/utils/action-info';
import { ActionType } from 'cio/models/action';
import { changeFromLast, trendPositivity, trendIconClass } from 'cio/utils/metrics/trends';
import { Measure } from 'cio/utils/metrics/common';
import { Metric } from 'cio/utils/metrics/models/metric';
import { mapPercentDataPoints } from 'cio/utils/graphs';

interface DashboardPageExpandableChartSectionArgs {
  environmentModel: EnvironmentModel;
  isLoading: boolean;
  isUpdating: boolean;
  color: string;
  chartLineColor: string;
  chartGradientColor: string;
  metric: Metric;
  measure: Measure;
  availableMeasures: string[];
  selectedChannel: string;
  expandedMeasure: string;
  isRate: boolean;
  onToggleExpansion: (metric: string | null) => unknown;
  onTrackChartHover: (data: Record<string, any>) => unknown;
}

const COLLAPSE_DURATION = 250;

export default class DashboardPageExpandableChartSection extends Component<DashboardPageExpandableChartSectionArgs> {
  COLLAPSE_DURATION = COLLAPSE_DURATION;

  get selectedChannelLabel() {
    return actionInfo(`${this.args.selectedChannel}_action` as ActionType).name;
  }

  get measureLabel() {
    return (DELIVERY_METRICS_MEASURE_LABELS as any)[underscore(this.args.measure)];
  }

  get collapsed() {
    return this.args.measure !== this.args.expandedMeasure;
  }

  get isMeasureAvailable() {
    return this.args.availableMeasures.includes(this.args.measure);
  }

  get changeFromLast() {
    if (!this.args.metric) {
      return null;
    }

    let measureAccessor: 'rates' | 'totals' = this.args.isRate ? 'rates' : 'totals';

    let measureData = this.args.metric[this.args.measure]?.[measureAccessor] ?? [];

    return changeFromLast(measureData);
  }

  get trendPositivity() {
    if (!this.changeFromLast) {
      return null;
    }

    return trendPositivity(this.args.measure, this.changeFromLast);
  }

  get trendIconClass() {
    return trendIconClass(this.trendPositivity);
  }

  get values() {
    if (!this.args.metric) {
      return [];
    }

    if (this.args.isRate) {
      return mapPercentDataPoints(this.args.metric[this.args.measure]?.rates ?? []);
    }

    return this.args.metric[this.args.measure]?.totals ?? [];
  }

  *collapseTransition({ removedSprites }: { removedSprites: Sprite[] }) {
    // This makes sure the disappearing panel body doesn't disappear instantly.
    // Otherwise the container will be empty while it animates, which we don't
    // want.
    // eslint-disable-next-line
    for (let _ of removedSprites) {
      if (!Ember.testing) {
        yield wait(COLLAPSE_DURATION);
      }
    }
  }

  chartOptions = {
    tooltip: {
      headerClasses: 'text-xs font-normal',
    },
    time: {
      timezone: 'Etc/GMT+4',
    },
  };

  @action
  toggleCollapseState(event: MouseEvent) {
    let { currentTarget } = event;
    if ((event?.target as HTMLElement)?.tagName === 'A') {
      // If the click originated from a link inside this item, ignore the
      // collapse functionality and let the routing handle the link
      return;
    }

    if (!this.collapsed) {
      this.args.onToggleExpansion(null);
    } else {
      this.args.onToggleExpansion(this.args.measure);
    }

    // We want to know whether the click event was fired from a keyboard
    // interaction, or from a mouse interaction. Unfortunately there's no clear
    // indicator of that.
    // For keyboard clicks, the x and y props are both set to 0, so we base our
    // check on that.
    if (event.x !== 0 && event.y !== 0) {
      // if this is a mouse event, remove focus from the element so that it
      // doesn't show the outline.
      // For keyboard events we want to keep focus, so that users can continue
      // tabbing
      (currentTarget as HTMLElement)?.blur();
    }
  }

  @action
  onTrackChartHover(time: number) {
    this.args.onTrackChartHover({
      metric: this.args.measure,
      channel: this.args.selectedChannel,
      time,
    });
  }
}
